import { fetch } from '@maxtropy/components';
import qs from 'qs';
import { Key } from 'react';
import { OuStaffType, OuStatus } from '@/shared/const';
import { Operator } from '@/shared/types';
import { PageRequest, PageResponse } from './page';

export interface QueryProductionUnitListRequest extends PageRequest {
  nameOrSerialNum?: string;
  ifCancel?: boolean;
}

export interface ProductionUnitListProps {
  homePage?: string;
  id: number;
  name: string;
  ouTypeId: string;
  mcid: string;
  tenantId: string;
  tenantName: string;
  customerName: string;
  directorName: string;
  directorPhone: string;
  remark: string;
  updateTime: string;
  updateByUsername: string;
  updateByUserId: string;
  updateSource: Operator;
  serialNumber: string;
  ouTypeName: string;
  deviceNum: string;
  status: OuStatus;
  userType: OuStaffType;
}

export const queryProductionUnitList = (params: QueryProductionUnitListRequest) =>
  fetch<PageResponse<ProductionUnitListProps>>(
    `/api/v2/ou/page`,
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );

interface QueryUserListRequest extends PageRequest {
  name?: string;
  staffCode?: string;
  phone?: string;
  username?: string;
  humanCode?: string;
  mcid: string;
}

export interface UserListProps {
  staffId: number;
  id: number;
  humanCode: string;
  staffType: any;
  mcid: string;
  muid: string;
  status: number;
  staffCode: string;
  username: string;
  name: string;
  phone: string;
  orgName: string;
  orgFullName: string;
}

export const queryUserList = (params: QueryUserListRequest) =>
  fetch<PageResponse<UserListProps>>(
    `/api/v2/ou/user/page`,
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );

export interface OuStaffListProps {
  staffId: number;
  id: number;
  staffType: OuStaffType;
  muid: string;
  username: string;
  staffCode: string;
  name: string;
  phone: string;
  orgName: string;
  humanCode: string;
}

export const queryOuStaffList = (id: string) =>
  fetch<{ list: OuStaffListProps[] }>(
    `/api/v2/ou/loadStaffInfoList`,
    {
      method: 'POST',
      body: JSON.stringify({ id }),
    },
    true
  );

interface QueryOuStaffPage extends PageRequest {
  id: Key;
}

export const queryOuStaffPage = (params: QueryOuStaffPage) =>
  fetch<PageResponse<OuStaffListProps>>(
    `/api/v2/ou/loadStaffInfoPage`,
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );

export interface OuTypeListProps {
  id: number;
  name: string;
  key: 'PRODUCTION_LINE' | 'MICRONET';
}

export const queryOuTypeList = () =>
  fetch<{ list: OuTypeListProps[] }>(
    `/api/v2/ou/ouType/list`,
    {
      method: 'POST',
    },
    true
  ).then(res => res.list ?? []);

interface AddOuBody {
  name: string;
  ouTypeId: string;
  mcid: string;
  directorName: string;
  directorPhone: string;
  remark: string;
  memberIds: number[];
  adminIds: number[];
}

export const addOu = (body: AddOuBody) =>
  fetch<never>(`/api/v2/ou/add`, { method: 'POST', body: JSON.stringify(body) }, true);

export interface OuInfoProps {
  id: number;
  name: string;
  ouTypeId: number;
  ouTypeName: string;
  status: OuStatus;
  tenantId: string;
  tenantName: string;
  tenantCode?: any;
  mcid: string;
  deviceNum?: any;
  customerName: string;
  directorName: string;
  directorPhone: string;
  remark: string;
  createTime: string;
  updateTime: string;
  updateByUsername: string;
  updateByUserId: string;
  updateSource: number;
  serialNumber: string;
  userType: OuStaffType;
  tenantMcid?: string;
}

export const getOuInfo = (id: string) =>
  fetch<OuInfoProps>(
    `/api/v2/ou/info`,
    {
      method: 'POST',
      body: JSON.stringify({ id }),
    },
    true
  );

interface UpdateOuBody {
  id: string;
  name: string;
  directorName: string;
  directorPhone: string;
  remark: string;
  memberIds: number[];
  adminIds: number[];
}

export const updateOu = (body: UpdateOuBody) =>
  fetch<never>(`/api/v2/ou/edit`, { method: 'POST', body: JSON.stringify(body) }, true);

interface UpdateOuState {
  state: OuStatus;
  password: string;
  id: number;
}

export const updateOuState = (body: UpdateOuState) =>
  fetch<never>(
    `/api/v2/ou/state`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    true
  );

export const getStaffInfo = () =>
  fetch<OuStaffListProps>(
    `/api/v2/ou/loadStaffInfo`,
    {
      method: 'POST',
    },
    true
  );

export interface OuListResponse {
  id?: number;
  name?: string;
  customerName?: string;
  status?: OuStatus;
}

export const getOuListOwned = () =>
  fetch<{ list: OuListResponse[] }>(
    `/api/v2/ou/list/owned`,
    {
      method: 'POST',
    },
    true
  ).then(res => res.list ?? []);

export const getOuListByMcid = (mcid: string) =>
  fetch<{ list: OuInfoProps[] }>(
    `/api/v2/ou/list-by-mcid`,
    {
      method: 'POST',
      body: JSON.stringify({ request: mcid }),
    },
    true
  ).then(res => res.list ?? []);

export const getOuByFcs = () =>
  fetch<{ list: OuInfoProps[] }>(
    `/api/v2/ou/list/fcs`,
    {
      method: 'POST',
    },
    true
  );

export interface DeviceListByOuProps {
  code: string;
  createTime: string;
  edgeDeviceId: number;
  id: number;
  modelId: number;
  modelName: string;
  name: string;
  sn: string;
  status: number;
  typeId: number;
  typeName: string;
}

export const getDeviceListByOu = (id: number) =>
  fetch<{ list: DeviceListByOuProps[] }>(
    `/api/v2/ou/device/list/electricityMeter`,
    {
      method: 'POST',
      body: JSON.stringify({ id }),
    },
    true
  );

export interface OuListByUetRes extends ProductionUnitListProps {
  tenantMcid: Key; // 租户mcid
  createTime: string; // 创建时间
  updateTime: string; // 更新时间
  manageUpdateByUsername: string; // 操作人(实施)
  manageUpdateByUserId: Key; // 操作人id(实施)
  manageUpdateTime: string; // 更新时间(实施)
}

// 获取拓扑运营单元列表
export const getUetOuList = async (id: Key) => {
  const res = await fetch<{ list: OuListByUetRes[] }>(
    `/api/v2/uet/getOuList`,
    {
      method: 'POST',
      body: JSON.stringify({ id }),
    },
    true
  );
  return res.list;
};
